import React, { useState, useImperativeHandle, forwardRef, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { currentSelectedLanguage } from "../../../../StateManagement/actions";
import AllowEditingPopup, {
  formatLanguageText,
  getEditEnabledCount,
  getHistoryEnabledCount,
} from "./AllowEditingPopup";
import "./GridGuardOptions.css";

export const GridGuardDefaultOptions = {
  allowCopy: false,
  allowEdit: false,
  editMode: "none",
  authorizedEditors: [],
  showHistory: false,
  historyVisibility: "all",
  authorizedHistoryViewers: [],
  showHiddenMergedCells: false,
  allowSortAndFilter: false,
  showFormulae: false,
  makeLinksClickable: false,
};
export const resetAllowEditing = (options) => ({
  ...(options || GridGuardDefaultOptions),
  allowEdit: false,
  editMode: "none",
  authorizedEditors: [],
  showHistory: false,
  historyVisibility: "all",
  authorizedHistoryViewers: [],
});

const pickEditingSettings = (options) => ({
  editMode: options.editMode,
  authorizedEditors: options.authorizedEditors || [],
  showHistory: options.showHistory,
  historyVisibility: options.historyVisibility,
  authorizedHistoryViewers: options.authorizedHistoryViewers || [],
});

const isSecurityLevelOne = () => {
  const level2 = document.getElementById("level2");
  const level3 = document.getElementById("level3");
  if (!level2) return false;
  return !level2.checked && !(level3 && level3.checked);
};

const getOptionGroups = (selectedLanguage) => [
  {
    key: "layoutVisibility",
    title: selectedLanguage.LstLanguageCodes.UI_GG_LayoutVisibility,
    color: "purple",
    options: [
      {
        id: "showHiddenMergedCells",
        label: selectedLanguage.LstLanguageCodes.UI_GG_ShowHiddenCells,
        desc: selectedLanguage.LstLanguageCodes.UI_GG_ShowHiddenCellsDesc,
        icon: "visibility",
      },
      {
        id: "showFormulae",
        label: selectedLanguage.LstLanguageCodes.UI_GG_ShowFormulas,
        desc: selectedLanguage.LstLanguageCodes.UI_GG_ShowFormulasDesc,
        icon: "code",
      },
    ],
  },
  {
    key: "interaction",
    title: selectedLanguage.LstLanguageCodes.UI_GG_Interaction,
    color: "emerald",
    options: [
      {
        id: "allowSortAndFilter",
        label: selectedLanguage.LstLanguageCodes.UI_GG_EnableSortFilter,
        desc: selectedLanguage.LstLanguageCodes.UI_GG_EnableSortFilterDesc,
        icon: "filter_alt",
      },
      {
        id: "makeLinksClickable",
        label: selectedLanguage.LstLanguageCodes.UI_GG_EnableHyperlinks,
        desc: selectedLanguage.LstLanguageCodes.UI_GG_EnableHyperlinksDesc,
        icon: "open_in_new",
      },
    ],
  },
  {
    key: "dataAccess",
    title: selectedLanguage.LstLanguageCodes.UI_GG_DataAccess,
    color: "blue",
    options: [
      {
        id: "allowEdit",
        label: selectedLanguage.LstLanguageCodes.UI_GG_AllowEditing,
        desc: selectedLanguage.LstLanguageCodes.UI_GG_AllowEditingDesc,
        icon: "edit",
      },
      {
        id: "allowCopy",
        label: selectedLanguage.LstLanguageCodes.UI_GG_AllowCopy,
        desc: selectedLanguage.LstLanguageCodes.UI_GG_AllowCopyDesc,
        icon: "content_copy",
      },
    ],
  },
];

const GridGuardOptions = forwardRef(({ readers, onClose, isAllowEditingDisabled }, ref) => {
  const selectedLanguage = useSelector(currentSelectedLanguage);

  const [isOpen, setIsOpen] = useState(false);
  const [localOptions, setLocalOptions] = useState(GridGuardDefaultOptions);
  const [isAllowEditingPopupOpen, setIsAllowEditingPopupOpen] = useState(false);
  const [popupOptions, setPopupOptions] = useState(GridGuardDefaultOptions);
  const [isEditingLocked, setIsEditingLocked] = useState(false);
  const savedEditingRef = useRef(null);
  const availableReaders = useMemo(() => Array.from(new Set(readers || [])), [readers]);
  const optionGroups = getOptionGroups(selectedLanguage);

  const withEditingDisabled = (options) => resetAllowEditing(options);

  const normalizeOptions = (options, locked) => {
    const normalized = {
      ...GridGuardDefaultOptions,
      ...(options || {}),
      editMode: options?.editMode || (options?.allowEdit ? "all" : "none"),
      authorizedEditors: options?.authorizedEditors || [],
      historyVisibility: options?.historyVisibility || "all",
      authorizedHistoryViewers: options?.authorizedHistoryViewers || [],
    };
    return locked ? withEditingDisabled(normalized) : normalized;
  };

  // Drops readers that were removed from the To field so stale emails are never kept or counted.
  const keepCurrentReaders = (list) => {
    const current = new Set(availableReaders.map((email) => email.toLowerCase()));
    return (list || []).filter((email) => current.has(String(email).toLowerCase()));
  };

  useImperativeHandle(ref, () => ({
    showModal: (currentOptions) => {
      const locked = isAllowEditingDisabled ?? isSecurityLevelOne();
      setIsEditingLocked(locked);
      const normalized = normalizeOptions(currentOptions, locked);
      savedEditingRef.current = normalized.allowEdit ? pickEditingSettings(normalized) : null;
      setLocalOptions(normalized);
      setIsOpen(true);
    },
    closeModal: () => {
      setIsOpen(false);
      setIsAllowEditingPopupOpen(false);
    },
  }));

  const handleOptionClick = (optionId) => {
    if (optionId === "allowEdit" && isEditingLocked) return;
    if (optionId === "allowEdit") {
      if (localOptions.allowEdit) {
        // Turning OFF applies immediately, but the saved settings are remembered for re-checking.
        savedEditingRef.current = pickEditingSettings(localOptions);
        setLocalOptions((prev) => withEditingDisabled(prev));
      } else {
        // Turning ON is only a draft until the popup is saved; Cancel leaves it unchecked.
        // Restores the previously saved settings; falls back to the defaults (All readers) if there are none.
        const restored = savedEditingRef.current ? { ...localOptions, ...savedEditingRef.current } : localOptions;
        setPopupOptions({
          ...restored,
          allowEdit: true,
          editMode: restored.editMode === "none" ? "all" : restored.editMode,
        });
        setIsAllowEditingPopupOpen(true);
      }
      return;
    }
    setLocalOptions((prev) => ({ ...prev, [optionId]: !prev[optionId] }));
  };

  const handleSettingsClick = (e, optionId) => {
    e.stopPropagation();
    if (optionId === "allowEdit" && !isEditingLocked) {
      setPopupOptions(localOptions);
      setIsAllowEditingPopupOpen(true);
    }
  };

  const handleAllowEditingSave = (updatedOptions) => {
    savedEditingRef.current = updatedOptions.allowEdit ? pickEditingSettings(updatedOptions) : null;
    setLocalOptions(updatedOptions);
    setIsAllowEditingPopupOpen(false);
  };

  const handleCancel = (e) => {
    setIsOpen(false);
    if (onClose) onClose(e, null, false);
  };
  const handleSave = (e) => {
    let next = {
      ...localOptions,
      authorizedEditors: keepCurrentReaders(localOptions.authorizedEditors),
      authorizedHistoryViewers: keepCurrentReaders(localOptions.authorizedHistoryViewers),
    };
    if (isEditingLocked) next = withEditingDisabled(next);

    // "Specific readers" with nobody available/selected grants nothing, so switch it off.
    if (next.allowEdit && next.editMode === "specific" && getEditEnabledCount(next, availableReaders) === 0) {
      next.allowEdit = false;
      next.editMode = "none";
    }

    setIsOpen(false);
    if (onClose) onClose(e, next, true);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="gg-modal-overlay" id="ggOptionsModal">
        <div className="gg-modal-content">
          {/* Header */}
          <div className="gg-modal-header">
            <div className="gg-header-left">
              <div className="gg-header-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-file-spreadsheet"
                  aria-hidden="true">
                  <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                  <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                  <path d="M8 13h2" />
                  <path d="M14 13h2" />
                  <path d="M8 17h2" />
                  <path d="M14 17h2" />
                </svg>
              </div>
              <div>
                <h4 className="gg-title">{selectedLanguage.LstLanguageCodes.UI_GridGuard}</h4>
                <p className="gg-subtitle">
                  {selectedLanguage.LstLanguageCodes.UI_GG_SpreadsheetSecurityEngine}
                </p>
              </div>
            </div>
            <button type="button" className="gg-close-btn" onClick={handleCancel} title={selectedLanguage.LstLanguageCodes.UI_Close}>
              <span className="material-icons">close</span>
            </button>
          </div>

          {/* Body */}
          <div className="gg-modal-body">
            <div className="gg-groups">
              {optionGroups.map((group) => (
                <div key={group.key} className="gg-group">
                  <div className="gg-group-heading">
                    <span className={`gg-group-bar gg-color-${group.color}`} />
                    <h5 className="gg-group-title">{group.title}</h5>
                  </div>

                  <div className="gg-option-grid">
                    {group.options.map((option) => {
                      const isDisabled = option.id === "allowEdit" && isEditingLocked;
                      const isEnabled = !isDisabled && !!localOptions[option.id];
                      const hasSettings = option.id === "allowEdit";
                      return (
                        <div
                          key={option.id}
                          id={`gg_${option.id}`}
                          className={`gg-option-card ${isEnabled ? "gg-selected" : ""} ${isDisabled ? "gg-disabled" : ""}`}
                          aria-disabled={isDisabled}
                          onClick={() => handleOptionClick(option.id)}>
                          <div className="gg-option-icon">
                            <span className="material-icons">{option.icon}</span>
                          </div>
                          <div className="gg-option-text">
                            <p className="gg-option-label">{option.label}</p>
                            <p className="gg-option-desc" title={option.desc}>
                              {option.desc}
                            </p>
                          </div>
                          <div className="gg-option-check">
                            {isEnabled && <span className="material-icons">check</span>}
                          </div>
                          {hasSettings && isEnabled && (
                            <button
                              type="button"
                              className="gg-settings-btn"
                              onClick={(e) => handleSettingsClick(e, option.id)}>
                              <span className="material-icons">settings</span>
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="gg-modal-footer">
            <div className="gg-chip-row">
              {localOptions.allowEdit && (
                <span className="gg-chip gg-chip-green">
                  <span className="material-icons">edit</span>
                  {formatLanguageText(
                    selectedLanguage.LstLanguageCodes.UI_GG_EditSummary,
                    localOptions.editMode === "all"
                      ? selectedLanguage.LstLanguageCodes.UI_GG_AllReaders
                      : formatLanguageText(
                        selectedLanguage.LstLanguageCodes.UI_GG_ReadersCount,
                        getEditEnabledCount(localOptions, availableReaders)
                      )
                  )}
                </span>
              )}
              {localOptions.showHistory && (localOptions.historyVisibility === "all" || getHistoryEnabledCount(localOptions, availableReaders) > 0) && (
                <span className="gg-chip gg-chip-indigo">
                  <span className="material-icons">history</span>
                  {formatLanguageText(
                    selectedLanguage.LstLanguageCodes.UI_GG_HistorySummary,
                    localOptions.historyVisibility === "all"
                      ? selectedLanguage.LstLanguageCodes.UI_GG_AllReaders
                      : formatLanguageText(
                        selectedLanguage.LstLanguageCodes.UI_GG_ReadersCount,
                        getHistoryEnabledCount(localOptions, availableReaders)
                      )
                  )}
                </span>
              )}
            </div>
            <div className="gg-footer-actions">
              <button type="button" className="gg-btn gg-btn-cancel" onClick={handleCancel}>
                {selectedLanguage.LstLanguageCodes.UI_Cancel}
              </button>
              <button
                type="button"
                className="gg-btn gg-btn-primary"
                onClick={handleSave}>
                {selectedLanguage.LstLanguageCodes.UI_Save}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AllowEditingPopup
        isOpen={isAllowEditingPopupOpen}
        options={popupOptions}
        readers={availableReaders}
        onClose={() => setIsAllowEditingPopupOpen(false)}
        onSave={handleAllowEditingSave}
      />

    </>
  );
});

export const GridGuardScope = {
  AllReaders: "AllReaders",
  SpecificReaders: "SpecificReaders",
};

const toUniqueEmails = (list) => {
  const seen = new Set();
  return (list || [])
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter((email) => {
      const key = email.toLowerCase();
      if (!email || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

/**
 * Builds the GridGuardOptions object sent to the API.
 * AllowEditing / ShowChangeHistory are false (off) or the scope itself: "AllReaders" |
 * "SpecificReaders". Recipients carry the resolved per-reader flags and are only sent when
 * one of the scopes is SpecificReaders.
 */
export const buildGridGuardPayload = (options, readers, editingDisabled) => {
  const source = {
    ...GridGuardDefaultOptions,
    ...(options || {}),
  };

  const allReaders = toUniqueEmails(readers);
  const readerKeys = new Set(allReaders.map((email) => email.toLowerCase()));

  const keepKnownReaders = (list) =>
    toUniqueEmails(list).filter(
      (email) => readerKeys.size === 0 || readerKeys.has(email.toLowerCase())
    );

  // 1. Who can edit.
  const editEnabled = !(editingDisabled ?? isSecurityLevelOne()) && !!source.allowEdit && source.editMode !== "none";
  const editAll = editEnabled && source.editMode === "all";
  const editors = !editEnabled
    ? []
    : editAll
      ? allReaders
      : keepKnownReaders(source.authorizedEditors);

  // 2. Who can see change history.
  const historyOn = !!source.showHistory && !(editingDisabled ?? isSecurityLevelOne());
  const historyAll = historyOn && source.historyVisibility === "all";
  let historyViewers = [];

  if (historyAll) {
    historyViewers = allReaders;
  } else if (historyOn && source.historyVisibility === "editorsOnly") {
    historyViewers = editors;
  } else if (historyOn && source.historyVisibility === "specific") {
    historyViewers = keepKnownReaders(source.authorizedHistoryViewers);
  }

  // 3. Scopes. null = the feature is off (a "specific" scope with nobody selected is off).
  const editingScope = !editEnabled
    ? null
    : editAll
      ? GridGuardScope.AllReaders
      : editors.length > 0
        ? GridGuardScope.SpecificReaders
        : null;

  const historyScope = !historyOn
    ? null
    : historyAll
      ? GridGuardScope.AllReaders
      : historyViewers.length > 0
        ? GridGuardScope.SpecificReaders
        : null;

  const payload = {
    AllowCopy: !!source.allowCopy,
    ShowHiddenCells: !!source.showHiddenMergedCells,
    ShowFormulas: !!source.showFormulae,
    EnableSortFilter: !!source.allowSortAndFilter,
    EnableHyperlinks: !!source.makeLinksClickable,
    AllowEditing: editingScope ?? false,
    ShowChangeHistory: historyScope ?? false,
  };

  // 4. Per-reader flags, only needed when a scope is limited to specific readers.
  const needsRecipients =
    editingScope === GridGuardScope.SpecificReaders ||
    historyScope === GridGuardScope.SpecificReaders;

  if (needsRecipients) {
    const editorKeys = new Set(editors.map((email) => email.toLowerCase()));
    const viewerKeys = new Set(historyViewers.map((email) => email.toLowerCase()));

    payload.Recipients = toUniqueEmails([...editors, ...historyViewers]).map((email) => {
      const key = email.toLowerCase();
      return {
        Email: email,
        AllowEditing: editorKeys.has(key),
        ShowChangeHistory: viewerKeys.has(key),
      };
    });
  }

  return payload;
};

const pickValue = (source, name) => {
  if (!source) return undefined;
  if (source[name] !== undefined) return source[name];
  const camel = name.charAt(0).toLowerCase() + name.slice(1);
  return source[camel];
};

const toScope = (value) => {
  if (value === true) return GridGuardScope.AllReaders;
  if (value === GridGuardScope.AllReaders || value === GridGuardScope.SpecificReaders) return value;
  return null;
};

/**
 * Reverse of buildGridGuardPayload: turns the GridGuardOptions the API returns (either casing)
 * into the shape the options modal and the version-history view work with.
 * Returns null when there is nothing stored.
 */
export const parseGridGuardPayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  const editScope = toScope(pickValue(payload, "AllowEditing"));
  const historyScope = toScope(pickValue(payload, "ShowChangeHistory"));
  const recipients = (pickValue(payload, "Recipients") || []).filter(Boolean);
  const emailsWhere = (flag) =>
    toUniqueEmails(
      recipients
        .filter((item) => pickValue(item, flag) === true)
        .map((item) => pickValue(item, "Email"))
    );

  return {
    ...GridGuardDefaultOptions,
    allowCopy: !!pickValue(payload, "AllowCopy"),
    showHiddenMergedCells: !!pickValue(payload, "ShowHiddenCells"),
    showFormulae: !!pickValue(payload, "ShowFormulas"),
    allowSortAndFilter: !!pickValue(payload, "EnableSortFilter"),
    makeLinksClickable: !!pickValue(payload, "EnableHyperlinks"),
    allowEdit: !!editScope,
    editMode: !editScope ? "none" : editScope === GridGuardScope.AllReaders ? "all" : "specific",
    authorizedEditors: editScope === GridGuardScope.SpecificReaders ? emailsWhere("AllowEditing") : [],
    showHistory: !!historyScope,
    historyVisibility: historyScope === GridGuardScope.SpecificReaders ? "specific" : "all",
    authorizedHistoryViewers: historyScope === GridGuardScope.SpecificReaders ? emailsWhere("ShowChangeHistory") : [],
  };
};

export default GridGuardOptions;